import type React from "react";
import { useEffect, useRef, useState } from "react";

import { bookingSelector } from "@redux/booking/selectors";
import {
	useCurrentSongSelector,
	useNextSongSelector,
	useWaitingListSelector,
} from "@redux/player";
import { setBamSessionUserSessionStarted } from "@redux/session";
import {
	isSessionStartedSelector,
	isStorageActivatedSelector,
} from "@redux/session/selectors";
import { userTypeSelector } from "@redux/user/selectors";
import * as Sentry from "@sentry/browser";
import { useDispatch, useSelector } from "react-redux";
import { useBoolean } from "react-use";

import ControlBarEventsWrapper from "components/shared/ControlBarEventsWrapper";
import { sendMultiScreenEventToTv } from "services/helpers/multiScreen";
import { shouldSendView } from "services/helpers/view";
import { useTvToIpadMultiScreenEventsListener } from "services/hooks/useMultiScreenEventListener";
import { updateBookingStatus } from "services/networking/booking";
import { getSong } from "services/networking/songs";
import { addB2bView } from "services/networking/views";
import { BookingStatus } from "shared-types/booking";
import { PlayerEvent, type PlayerSongDetails } from "shared-types/player-event";

import ControlBarContent from "./ControlBarContent";

const ControlBar: React.FC = () => {
	const dispatch = useDispatch();
	const [currentSongDetails, setCurrentSongDetails] =
		useState<PlayerSongDetails | null>(null);
	const [isVocalGuideAvailable, setIsVocalGuideAvailable] = useBoolean(false);
	const currentSong = useCurrentSongSelector();
	const nextSong = useNextSongSelector();
	const isStorageActivated = useSelector(isStorageActivatedSelector);
	const songViewSent = useRef(false);
	const userType = useSelector(userTypeSelector);
	const isSessionStarted = useSelector(isSessionStartedSelector);
	const booking = useSelector(bookingSelector);
	const waitingList = useWaitingListSelector();

	useEffect(() => {
		const fetchCurrentSongDetails = async () => {
			if (currentSong) {
				const timestamp = Date.now();
				const song = await getSong(currentSong.id);

				const songDetails: PlayerSongDetails = {
					title: song.title,
					artistName: song.artist?.name,
					singerName: currentSong?.singerName,
				};

				sendMultiScreenEventToTv(
					PlayerEvent.CURRENT_SONG_DETAILS,
					JSON.stringify(songDetails),
					timestamp,
				);
				setCurrentSongDetails(songDetails);
			} else {
				sendMultiScreenEventToTv(PlayerEvent.CURRENT_SONG_DETAILS, null);
				setCurrentSongDetails(null);
			}
			sendMultiScreenEventToTv(
				PlayerEvent.IS_STORAGE_ACTIVATED,
				JSON.stringify(isStorageActivated),
			);
		};

		fetchCurrentSongDetails();
	}, [currentSong, isStorageActivated]);

	useEffect(() => {
		const fetchNextSongDetails = async () => {
			if (nextSong) {
				const timestamp = Date.now();
				const song = await getSong(nextSong.id);
				const songDetails: PlayerSongDetails = {
					title: song.title,
					artistName: song.artist?.name,
					singerName: nextSong?.singerName,
				};

				sendMultiScreenEventToTv(
					PlayerEvent.NEXT_SONG_DETAILS,
					JSON.stringify(songDetails),
					timestamp,
				);
			} else {
				sendMultiScreenEventToTv(PlayerEvent.NEXT_SONG_DETAILS, null);
			}
		};

		fetchNextSongDetails();
	}, [nextSong]);

	useEffect(() => {
		// Reset songViewSent state on song change
		songViewSent.current = false;
	}, [currentSong]);

	useEffect(() => {
		// Reset vocal guide state on song change
		setIsVocalGuideAvailable(false);
	}, [currentSong, setIsVocalGuideAvailable]);

	const currentTimeHandler = async (event: MessageEvent) => {
		if (
			shouldSendView(
				Number.parseInt(event.data, 10),
				currentSong,
				songViewSent.current,
			)
		) {
			// SongView is set by default to true because if we wait for the end of the request,
			// multiple requests could be sent in the meantime as this function is called every second
			songViewSent.current = true;
			try {
				if (!isSessionStarted && booking) {
					updateBookingStatus(booking.bookingId, BookingStatus.IN_PROGRESS);
					dispatch(setBamSessionUserSessionStarted(true));
				}

				await addB2bView(currentSong.id, userType);
			} catch (error) {
				// only in the case of an error, do we set SongView back to false
				songViewSent.current = false;
				Sentry.captureException(error);
			}
		}
	};
	useTvToIpadMultiScreenEventsListener({
		[PlayerEvent.CURRENT_TIME]: currentTimeHandler,
		[PlayerEvent.ON_VOCAL_GUIDE_LOADED]: () => {
			setIsVocalGuideAvailable(true);
		},
	});

	const songTitle = currentSongDetails?.title;
	const songArtistName = currentSongDetails?.artistName;
	const controlsDisabled = !currentSong && waitingList.length === 0;

	return (
		<ControlBarEventsWrapper
			ControlBarContentComponent={ControlBarContent}
			sendPlayerEvent={sendMultiScreenEventToTv}
			usePlayerEventsListener={useTvToIpadMultiScreenEventsListener}
			currentSong={currentSong}
			controlsDisabled={controlsDisabled}
			songTitle={songTitle}
			songArtistName={songArtistName}
			nextSong={nextSong}
			isVocalGuideAvailable={isVocalGuideAvailable}
		/>
	);
};

export default ControlBar;
